(() => {
  if (customElements.get('blog-sort')) return;

  const sorts = ['oldest', 'newest', 'title-asc', 'title-desc'];
  const validYear = value => value === 'all' || /^[1-9]\d{3}$/.test(value);
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  class BlogSort extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.select = this.querySelector('[data-blog-sort]');
      this.yearSelect = this.querySelector('[data-blog-year]');
      this.grid = this.querySelector('[data-blog-grid]');
      this.pagination = this.querySelector('[data-blog-pagination]');
      this.status = this.querySelector('[data-blog-sort-status]');
      this.retry = this.querySelector('[data-blog-sort-retry]');
      if (!this.select || !this.grid || !this.pagination) return;

      this.events = new AbortController();
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 10);
      this.collator = new Intl.Collator(document.documentElement.lang || 'en', {numeric: true, sensitivity: 'base'});
      this.sort = 'newest'; // The server-rendered fallback uses Shopify's native order.
      this.year = 'all';
      this.page = this.readPage();
      this.loadMore = this.dataset.paginationStyle === 'load_more';
      this.nativePagination = this.pagination.cloneNode(true);
      const options = {signal: this.events.signal};

      this.select.addEventListener('change', () => this.changeSort(this.select.value, 1, 'push', this.yearSelect?.value || 'all'), options);
      this.yearSelect?.addEventListener('change', () => this.changeSort(this.select.value, 1, 'push', this.yearSelect.value), options);
      this.retry.addEventListener('click', () => this.changeSort(this.requestedSort, this.requestedPage, 'replace', this.requestedYear), options);
      this.pagination.addEventListener('click', event => {
        const control = event.target.closest('[data-blog-page]');
        if (!control || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.showPage(Number(control.dataset.blogPage), true);
      }, options);
      window.addEventListener('popstate', () => this.changeSort(this.readSort(), this.readPage(), null, this.readYear()), options);
      this.changeSort(this.readSort(), this.page, 'replace', this.readYear());
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      this.requestVersion = (this.requestVersion || 0) + 1;
      this.articles = null;
      this.loading = null;
    }

    readSort() {
      const fromUrl = new URL(location.href).searchParams.get('blog_sort');
      if (sorts.includes(fromUrl)) return fromUrl;
      return sorts.includes(this.dataset.defaultSort) ? this.dataset.defaultSort : 'oldest';
    }

    readPage() {
      const page = Number(new URL(location.href).searchParams.get('page'));
      return Number.isInteger(page) && page > 0 ? page : 1;
    }

    readYear() {
      if (!this.yearSelect) return 'all';
      const fromUrl = new URL(location.href).searchParams.get('blog_year');
      if (validYear(fromUrl)) return fromUrl;
      const defaultYear = (this.dataset.defaultYear || '2024').trim().toLowerCase();
      return validYear(defaultYear) ? defaultYear : 'all';
    }

    articleYear(article) {
      // Prefer Shopify's publication year so filtering matches the date displayed by the store.
      const year = String(article.year || '');
      return /^[1-9]\d{3}$/.test(year) ? year : String(new Date(Number(article.published) * 1000).getUTCFullYear());
    }

    populateYears(articles) {
      if (!this.yearSelect) return;
      const years = new Set(articles.map(article => this.articleYear(article)));
      const defaultYear = (this.dataset.defaultYear || '2024').trim();
      if (/^[1-9]\d{3}$/.test(defaultYear)) years.add(defaultYear);
      if (this.requestedYear !== 'all') years.add(this.requestedYear);
      const fragment = document.createDocumentFragment();
      const all = element('option', '', 'All years');
      all.value = 'all';
      fragment.append(all);
      [...years].filter(year => /^[1-9]\d{3}$/.test(year)).sort((a, b) => Number(b) - Number(a)).forEach(year => {
        const option = element('option', '', year);
        option.value = year;
        fragment.append(option);
      });
      this.yearSelect.replaceChildren(fragment);
      this.yearSelect.value = this.requestedYear;
    }

    async loadArticles() {
      if (this.articles) return this.articles;
      if (this.loading) return this.loading;
      const signal = this.events.signal;
      const loading = this.loading = (async () => {
        const url = new URL(location.href);
        url.hash = '';
        url.searchParams.delete('sections');
        url.searchParams.delete('blog_sort');
        url.searchParams.delete('blog_year');
        url.searchParams.set('section_id', 'blog-sort-data');
        const articles = [];
        let pages = 1;
        let total = 0;
        for (let page = 1; page <= pages; page += 1) {
          url.searchParams.set('page', String(page));
          const response = await fetch(url, {signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('Article index unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const index = doc.querySelector('[data-blog-sort-index]');
          if (!index) throw new Error('Article index missing');
          const data = JSON.parse(index.textContent);
          if (!Array.isArray(data.articles) || Number(data.page) !== page) throw new Error('Invalid article index');
          pages = Number(data.pages);
          total = Number(data.total);
          if (!Number.isInteger(pages) || pages < 0 || pages > 100 || !Number.isInteger(total) || total < 0) {
            throw new Error('Invalid article count');
          }
          for (const item of data.articles) {
            if (item.id == null || typeof item.title !== 'string' || typeof item.html !== 'string' || !Number.isFinite(Number(item.published))) {
              throw new Error('Invalid article');
            }
            articles.push(item);
          }
        }
        if (articles.length !== total || new Set(articles.map(item => String(item.id))).size !== total) {
          throw new Error('Article archive changed while loading');
        }
        return articles;
      })();
      try {
        this.articles = await loading;
        return this.articles;
      } finally {
        if (this.loading === loading) this.loading = null;
      }
    }

    async changeSort(sort, page, historyMode, year = 'all') {
      const version = this.requestVersion = (this.requestVersion || 0) + 1;
      this.requestedSort = sorts.includes(sort) ? sort : 'oldest';
      this.requestedPage = page;
      this.requestedYear = validYear(year) ? year : 'all';
      this.select.value = this.requestedSort;
      this.select.disabled = true;
      if (this.yearSelect) this.yearSelect.disabled = true;
      this.retry.hidden = true;
      this.status.textContent = 'Loading posts…';
      this.setAttribute('data-loading', '');
      this.grid.setAttribute('aria-busy', 'true');
      try {
        const articles = await this.loadArticles();
        if (!this.isConnected || version !== this.requestVersion) return;
        this.populateYears(articles);
        const direction = this.requestedSort === 'title-desc' || this.requestedSort === 'newest' ? -1 : 1;
        const titleSort = this.requestedSort.startsWith('title-');
        this.ordered = articles.filter(article => this.requestedYear === 'all' || this.articleYear(article) === this.requestedYear).sort((a, b) => {
          const primary = titleSort ? this.collator.compare(a.title, b.title) : Number(a.published) - Number(b.published);
          return primary * direction || this.collator.compare(a.title, b.title) || String(a.id).localeCompare(String(b.id));
        });
        this.sort = this.requestedSort;
        this.year = this.requestedYear;
        this.showPage(page, false, historyMode);
      } catch (error) {
        if (error.name === 'AbortError' || !this.isConnected || version !== this.requestVersion) return;
        this.select.value = this.sort;
        if (this.yearSelect) this.yearSelect.value = this.year;
        this.status.textContent = 'Sorting is unavailable right now. Please try again.';
        this.retry.hidden = false;
        if (!this.ordered) this.pagination.replaceChildren(...this.nativePagination.cloneNode(true).childNodes);
      } finally {
        if (this.isConnected && version === this.requestVersion) {
          this.removeAttribute('data-loading');
          this.grid.removeAttribute('aria-busy');
          this.select.disabled = false;
          if (this.yearSelect) this.yearSelect.disabled = false;
        }
      }
    }

    makeCard(item, rank, featured, first) {
      const doc = new DOMParser().parseFromString(item.html, 'text/html');
      const card = doc.querySelector('.card-article');
      if (!card) throw new Error('Article card missing');
      card.querySelectorAll('script').forEach(node => node.remove());
      card.classList.toggle('blog__card-article--featured', featured);
      const index = card.querySelector('.card-article__index');
      if (index) index.textContent = String(rank).padStart(2, '0');
      if (this.dataset.showExcerpt !== 'true' && !featured) card.querySelector('.card-article__entry')?.remove();
      if (this.dataset.showAuthor !== 'true') card.querySelector('.article-card__author')?.remove();
      if (this.dataset.showDate !== 'true') card.querySelector('.article-card__date')?.remove();
      if (this.dataset.showAuthor !== 'true' && this.dataset.showDate !== 'true') card.querySelector('.card-article__meta')?.remove();
      const image = card.querySelector('img');
      if (image) image.loading = first ? 'eager' : 'lazy';
      return document.importNode(card, true);
    }

    showPage(page, interaction = false, historyMode = 'push') {
      if (!this.ordered) return;
      const pages = Math.max(1, Math.ceil(this.ordered.length / this.pageSize));
      const nextPage = Math.min(pages, Math.max(1, Number(page) || 1));
      const append = this.loadMore && interaction && nextPage === this.page + 1;
      const start = (nextPage - 1) * this.pageSize;
      const items = this.ordered.slice(start, start + this.pageSize);
      // Build the next page before replacing the current one, so errors preserve usable cards.
      const fragment = document.createDocumentFragment();
      items.forEach((item, index) => fragment.append(this.makeCard(item, start + index + 1, this.dataset.featureFirst === 'true' && index === 0, index === 0)));
      if (append) this.grid.append(fragment);
      else this.grid.replaceChildren(fragment);
      const emptyMessage = this.querySelector('.blog__articles-empty');
      if (emptyMessage) emptyMessage.hidden = true;
      this.page = nextPage;
      this.renderPagination(pages);
      const count = this.ordered.length;
      this.status.textContent = count
        ? `${count} ${count === 1 ? 'post' : 'posts'}${this.year === 'all' ? '' : ` in ${this.year}`}`
        : this.year === 'all' ? 'No posts found.' : `No posts found for ${this.year}. Choose another year or All years.`;
      this.closest('.blog')?.querySelectorAll('.blog__filters a').forEach(link => {
        const url = new URL(link.href);
        url.searchParams.set('blog_sort', this.sort);
        url.searchParams.set('blog_year', this.year);
        url.searchParams.delete('page');
        link.href = url.href;
      });
      if (historyMode) this.updateUrl(historyMode);
      if (interaction && !append) {
        this.select.focus({preventScroll: true});
        this.select.scrollIntoView({block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }
    }

    pageUrl(page) {
      const url = new URL(location.href);
      url.searchParams.set('blog_sort', this.sort);
      url.searchParams.set('blog_year', this.year);
      if (page === 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      return url.pathname + url.search + url.hash;
    }

    updateUrl(mode) {
      if (mode === 'push') history.pushState({}, '', this.pageUrl(this.page));
      else history.replaceState({}, '', this.pageUrl(this.page));
    }

    renderPagination(pages) {
      this.observer?.disconnect();
      this.pagination.replaceChildren();
      this.pagination.hidden = pages <= 1;
      if (pages <= 1) return;
      if (this.loadMore) {
        if (this.page >= pages) { this.pagination.hidden = true; return; }
        const button = element('button', 'button button--outlined', 'Load more');
        button.type = 'button';
        button.dataset.blogPage = this.page + 1;
        this.pagination.append(button);
        if (this.dataset.infinityScroll === 'true' && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting) && !this.hasAttribute('data-loading')) {
              this.observer.disconnect();
              this.showPage(this.page + 1, true, 'replace');
            }
          }, {rootMargin: '200px'});
          this.observer.observe(button);
        }
        return;
      }
      const nav = element('nav', 'pagination');
      nav.setAttribute('aria-label', 'Blog pages');
      const list = element('ul', 'list-unstyled');
      nav.append(list);
      const link = (page, label, name) => {
        const a = element('a', '', label);
        a.href = this.pageUrl(page);
        a.dataset.blogPage = page;
        a.setAttribute('aria-label', name || `Page ${page}`);
        const li = element('li');
        li.append(a);
        list.append(li);
      };
      if (this.page > 1) link(this.page - 1, '←', 'Previous page');
      const visible = new Set([1, pages, this.page - 1, this.page, this.page + 1]);
      let previous = 0;
      [...visible].filter(page => page >= 1 && page <= pages).sort((a, b) => a - b).forEach(page => {
        if (previous && page - previous > 1) {
          const li = element('li'); li.append(element('span', '', '…')); list.append(li);
        }
        if (page === this.page) {
          const current = element('span', 'pagination__current', String(page));
          current.setAttribute('aria-current', 'page');
          current.setAttribute('aria-label', `Page ${page}`);
          const li = element('li'); li.append(current); list.append(li);
        } else link(page, String(page));
        previous = page;
      });
      if (this.page < pages) link(this.page + 1, '→', 'Next page');
      this.pagination.append(nav);
    }
  }

  customElements.define('blog-sort', BlogSort);
})();
